import React, { useState, useEffect, useRef } from 'react';
import { Box, Paper, FormControl, Select, MenuItem, Alert, Backdrop, CircularProgress, Typography, InputLabel } from '@mui/material';
import ItemsGrid from './ItemsGrid';
import DataControls from './DataControls';
import AddTaskModal from './AddTaskModal';
import DeleteConfirmDialog from './DeleteConfirmDialog';
import { useAppStore } from '../state/store';
import { getModifiedTasks, getModifiedTakeaways } from '../utils/changeDetection';
import { meetingDataService } from '../services/meetingDataService';
import { salesforceService } from '../services/salesforceService';
import { userService } from '../services/userService';
import { SalesforceEvent } from '../types';

const GridTabs: React.FC = () => {
  const { 
    meetingId, 
    tasks, 
    takeaways, 
    originalTasks, 
    originalTakeaways, 
    tldr,
    executiveSummary,
    detailedSummary,
    originalTldr,
    originalExecutiveSummary,
    originalDetailedSummary,
    loadFromJson,
    deleteTask,
    deleteTakeaway
  } = useAppStore();
  const [filterType, setFilterType] = useState<'All' | 'Tasks' | 'Takeaways'>('All');
  
  // Selection state for tasks (lifted up to share with DataControls)
  const [selectedItemIds, setSelectedItemIds] = useState<Set<string>>(new Set());
  
  // Modal states
  const [addTaskModalOpen, setAddTaskModalOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [itemToDelete, setItemToDelete] = useState<{ id: string; name: string; type: 'task' | 'takeaway' } | null>(null);
  
  // Salesforce users for autocomplete
  const [salesforceUsers, setSalesforceUsers] = useState<any[]>([]);
  
  // Notification queue for stacked snackbars
  interface Notification {
    id: number;
    message: string;
    severity: 'success' | 'error' | 'warning' | 'info';
    open: boolean;
    autoHide: boolean;
  }
  const [notifications, setNotifications] = useState<Notification[]>([]);
  const notificationIdCounter = useRef(0);
  
  // Loading states
  const [sendingToSalesforce, setSendingToSalesforce] = useState(false);
  const [saving, setSaving] = useState(false);
  
  // Salesforce event dropdown state
  const [showEventDropdown, setShowEventDropdown] = useState(false);
  const [events, setEvents] = useState<SalesforceEvent[]>([]);
  const [selectedEventId, setSelectedEventId] = useState<string>('');
  const [loadingEvents, setLoadingEvents] = useState(false);
  const [resolvedEventId, setResolvedEventId] = useState<string | null>(null);

  // Helper to add notification to queue
  const addNotification = (message: string, severity: 'success' | 'error' | 'warning' | 'info', autoHideDuration?: number) => {
    const id = notificationIdCounter.current;
    notificationIdCounter.current += 1;
    
    // Determine auto-hide duration based on severity if not specified
    let hideDelay = autoHideDuration;
    if (hideDelay === undefined) {
      if (severity === 'success') {
        hideDelay = 15000; // 15 seconds for success
      } else if (severity === 'error') {
        hideDelay = 60000; // 1 minute for errors
      }
      // warning and info don't auto-hide by default
    }
    
    setNotifications(prev => [...prev, { id, message, severity, open: true, autoHide: hideDelay !== undefined }]);
    
    // Auto-hide after specified duration
    if (hideDelay !== undefined) {
      setTimeout(() => {
        closeNotification(id);
      }, hideDelay);
    }
  };

  // Helper to close notification
  const closeNotification = (id: number) => {
    setNotifications(prev => prev.map(n => n.id === id ? { ...n, open: false } : n));
    // Remove from array after animation
    setTimeout(() => {
      setNotifications(prev => prev.filter(n => n.id !== id));
    }, 500);
  };

  // Determine event dropdown visibility based on resolvedEventId from the API
  useEffect(() => {
    const checkResolvedEventAndFetchEvents = async () => {
      if (!meetingId) return;
      
      try {
        const response = await fetch(`/api/internal/meeting/${meetingId}/items`);
        if (!response.ok) {
          throw new Error('Failed to fetch meeting data');
        }
        
        const result = await response.json();
        const eventId = result.data?.meeting?.resolvedEventId;
        
        if (eventId) {
          setResolvedEventId(eventId);
          setShowEventDropdown(false);
        } else {
          setResolvedEventId(null);
          setShowEventDropdown(true);
          setLoadingEvents(true);
          try {
            const fetchedEvents = await salesforceService.fetchEvents();
            setEvents(fetchedEvents);
          } catch (error) {
            console.error('Error fetching Salesforce events:', error);
            setEvents([]);
          } finally {
            setLoadingEvents(false);
          }
        }
      } catch (error) {
        console.error('Error checking resolved event:', error);
        setShowEventDropdown(false);
      }
    };
    
    checkResolvedEventAndFetchEvents();
  }, [meetingId]);

  // Load Salesforce users for Add Task modal
  useEffect(() => {
    const loadUsers = async () => {
      try {
        const users = await userService.fetchUsers();
        setSalesforceUsers(users);
      } catch (error) {
        console.error('Failed to load Salesforce users:', error);
        setSalesforceUsers([]);
      }
    };
    
    loadUsers();
  }, []);

  const handleFilterChange = (event: any) => {
    setFilterType(event.target.value as 'All' | 'Tasks' | 'Takeaways');
    // Clear selection when switching filters
    setSelectedItemIds(new Set());
  };
  
  const handleEventChange = (event: any) => {
    setSelectedEventId(event.target.value);
  };

  const handleSaveChanges = async (): Promise<boolean> => {
    if (!meetingId) {
      console.log('No meeting ID');
      return false;
    }

    setSaving(true);

    try {
      console.log('Saving changes for meeting:', meetingId);
      
      // Separate new tasks (duplicated/converted) from existing tasks
      const newTasks = tasks.filter(task => task.isNew);
      const existingTasks = tasks.filter(task => !task.isNew);
      
      // Compare existing tasks to original state to find actual changes
      const changedTasks = getModifiedTasks(existingTasks, originalTasks);
      const changedTakeaways = getModifiedTakeaways(takeaways, originalTakeaways);
      
      // Check for summary changes
      const hasSummaryChanges = 
        tldr !== originalTldr ||
        executiveSummary !== originalExecutiveSummary ||
        detailedSummary !== originalDetailedSummary;
      
      // Don't send request if nothing actually changed and no new tasks
      if (changedTasks.length === 0 && changedTakeaways.length === 0 && newTasks.length === 0 && !hasSummaryChanges) {
        console.log('No actual changes detected');
        addNotification('No changes to save', 'info');
        return true; // Return true since no changes is considered success
      }
      
      console.log(`Saving ${newTasks.length} new tasks, ${changedTasks.length} modified tasks, and ${changedTakeaways.length} modified takeaways`);
      
      // Map new tasks to backend format (for creation)
      // For copied tasks, send ALL fields (editable + non-editable) from the original
      const newTasksData = newTasks.map(task => ({
        id: task.id, // Frontend-generated ID (required by backend)
        meetingId: meetingId,
        // Editable fields
        title: task.title,
        description: task.description,
        owner: task.owner,
        requester: task.requester,
        due: task.due,
        itemType: task.type,
        priority: task.priority,
        tags: task.tags,
        // Non-editable fields (copied from original)
        confidence: task.confidence,
        evidence: task.evidence,
        ownerCandidates: task.ownerCandidatesRaw || task.ownerCandidates, // Send original array if available
        // Set origin and originalId for converted/copied tasks
        origin: task.origin || 'native',
        originalId: task.originalId // For converted tasks, links to takeaway RowKey
      }));
      
      // Map modified tasks to backend format (for update)
      // For existing tasks, only send editable fields
      const modifiedTasks = changedTasks.map(task => ({
        id: task.id,
        meetingId: meetingId,
        // Only editable fields for updates
        title: task.title,
        description: task.description,
        owner: task.owner,
        requester: task.requester,
        due: task.due,
        itemType: task.type,
        priority: task.priority,
        tags: task.tags
      }));

      // Get converted takeaways (those marked as isConverted)
      const convertedTakeaways = takeaways.filter(t => t.isConverted);
      
      const modifiedTakeaways = [
        ...changedTakeaways.map(takeaway => ({
          id: takeaway.id,
          meetingId: meetingId,
          title: takeaway.name,
          description: takeaway.description,
          itemType: takeaway.type,
          tags: takeaway.tags
        })),
        // Send converted takeaways with isDeleted=true
        ...convertedTakeaways.map(takeaway => ({
          id: takeaway.id,
          meetingId: meetingId,
          isDeleted: true // Soft delete converted takeaways
        }))
      ];

      // Check for summary changes
      const hasTldrChange = tldr !== originalTldr;
      const hasExecutiveSummaryChange = executiveSummary !== originalExecutiveSummary;
      const hasDetailedSummaryChange = detailedSummary !== originalDetailedSummary;

      const requestBody: any = {
        newTasks: newTasksData.length > 0 ? newTasksData : undefined,
        tasks: modifiedTasks.length > 0 ? modifiedTasks : undefined,
        takeaways: modifiedTakeaways.length > 0 ? modifiedTakeaways : undefined
      };

      // Add summary changes if any
      if (hasTldrChange) {
        requestBody.tldr = tldr;
      }
      if (hasExecutiveSummaryChange) {
        requestBody.executiveSummary = executiveSummary;
      }
      if (hasDetailedSummaryChange) {
        requestBody.detailedSummary = detailedSummary;
      }

      // Call PUT /api/internal/meeting/{id}/items for updates and creates
      const response = await fetch(`/api/internal/meeting/${meetingId}/items`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include',
        body: JSON.stringify(requestBody)
      });

      if (!response.ok) {
        // Try to get error details from response
        let errorMessage = `Save failed: HTTP ${response.status}`;
        try {
          const errorData = await response.json();
          if (errorData.error) {
            errorMessage += ` - ${errorData.error}`;
          }
          if (errorData.details) {
            errorMessage += ` (${errorData.details})`;
          }
          console.error('Backend error response:', errorData);
        } catch (e) {
          // If we can't parse the error, use the status text
          errorMessage += ` - ${response.statusText}`;
        }
        throw new Error(errorMessage);
      }

      await response.json();
      
      // Reload the grid data from the backend to get the latest state
      console.log('Reloading grid data after save...');
      const data = await meetingDataService.loadMeeting(meetingId);
      
      // Update the store with the fresh data
      const distillOutput = {
        meeting_brief: data.meeting_brief,
        key_points: data.key_points,
        actions: data.actions,
        themes: data.themes,
        clarifications: [],
        metrics: {
          total_key_points: data.key_points?.length || 0,
          total_actions: data.actions?.length || 0,
          total_themes: data.themes?.length || 0,
          confidence_score_avg: 0.8
        }
      };
      loadFromJson(distillOutput, meetingId);
      
      // Clear selection
      setSelectedItemIds(new Set());
      
      // Show success message (auto-hide after 15 seconds)
      addNotification('Changes saved successfully', 'success');
      
      return true; // Success
    } catch (error) {
      console.error('Error saving changes:', error);
      const errorMsg = error instanceof Error ? error.message : 'Failed to save changes';
      
      addNotification(`Failed to save: ${errorMsg}`, 'error');
      
      return false; // Failure
    } finally {
      setSaving(false);
    }
  };

  // Handler for Add Task button
  const handleAddTask = () => {
    setAddTaskModalOpen(true);
  };

  // Handler for Add Task Modal submission
  const handleAddTaskSubmit = async (taskData: any) => {
    if (!meetingId) return;
    
    console.log('Adding new task with data:', taskData);
    setAddTaskModalOpen(false);
    setSaving(true);
    
    try {
      // Prepare task request
      const taskRequest = {
        id: crypto.randomUUID(), // Generate ID on frontend
        meetingId: meetingId,
        title: taskData.title,
        description: taskData.description,
        owner: taskData.owner,
        requester: taskData.requester,
        due: taskData.due,
        itemType: taskData.type,
        priority: taskData.priority,
        tags: taskData.tags,
        confidence: 0.8,
        evidence: [],
        origin: 'manual'
      };
      
      const response = await fetch(`/api/internal/meeting/${meetingId}/tasks`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include',
        body: JSON.stringify(taskRequest)
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.error || 'Failed to create task');
      }

      addNotification('Task created successfully', 'success');
      
      // Reload data to get the newly created task
      const data = await meetingDataService.loadMeeting(meetingId);
      const distillOutput = {
        meeting_brief: data.meeting_brief,
        key_points: data.key_points,
        actions: data.actions,
        themes: data.themes,
        clarifications: [],
        metrics: {
          total_key_points: data.key_points?.length || 0,
          total_actions: data.actions?.length || 0,
          total_themes: data.themes?.length || 0,
          confidence_score_avg: 0.8
        }
      };
      loadFromJson(distillOutput, meetingId);
      
    } catch (error) {
      console.error('Error creating task:', error);
      const errorMsg = error instanceof Error ? error.message : 'Failed to create task';
      addNotification(`Failed to create task: ${errorMsg}`, 'error');
    } finally {
      setSaving(false);
    }
  };

  // Handler for Delete icon click
  const handleDeleteItem = (item: any) => {
    setItemToDelete({
      id: item.id,
      name: item.title,
      type: item.rowType === 'Task' ? 'task' : 'takeaway'
    });
    setDeleteDialogOpen(true);
  };

  // Handler for Delete confirmation
  const handleDeleteConfirm = async () => {
    if (!itemToDelete || !meetingId) return;
    
    setDeleteDialogOpen(false);
    setSaving(true);
    
    try {
      const endpoint = itemToDelete.type === 'task' 
        ? `/api/internal/meeting/${meetingId}/tasks/${itemToDelete.id}`
        : `/api/internal/meeting/${meetingId}/takeaways/${itemToDelete.id}`;
      
      const response = await fetch(endpoint, {
        method: 'DELETE',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include',
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.error || `Failed to delete ${itemToDelete.type}`);
      }

      // Remove from local state immediately
      if (itemToDelete.type === 'task') {
        deleteTask(itemToDelete.id);
      } else {
        deleteTakeaway(itemToDelete.id);
      }

      addNotification(`${itemToDelete.type === 'task' ? 'Task' : 'Takeaway'} deleted successfully`, 'success');
      
      // Reload data to ensure consistency
      const data = await meetingDataService.loadMeeting(meetingId);
      const distillOutput = {
        meeting_brief: data.meeting_brief,
        key_points: data.key_points,
        actions: data.actions,
        themes: data.themes,
        clarifications: [],
        metrics: {
          total_key_points: data.key_points?.length || 0,
          total_actions: data.actions?.length || 0,
          total_themes: data.themes?.length || 0,
          confidence_score_avg: 0.8
        }
      };
      loadFromJson(distillOutput, meetingId);
      
    } catch (error) {
      console.error('Error deleting item:', error);
      const errorMsg = error instanceof Error ? error.message : 'Failed to delete item';
      addNotification(`Failed to delete: ${errorMsg}`, 'error');
    } finally {
      setSaving(false);
      setItemToDelete(null);
    }
  };

  const handleSendToSalesforce = async () => {
    try {
      setSendingToSalesforce(true);

      // Get the selected task IDs
      const selectedIds = Array.from(selectedItemIds);
      if (selectedIds.length === 0) {
        addNotification('No tasks selected to send to Salesforce', 'warning');
        return;
      }

      // Get task id and meetingId for selected items
      const selectedTasks = tasks.filter(task => selectedIds.includes(task.id));
      
      if (selectedTasks.length === 0) {
        addNotification('Selected items are not tasks', 'warning');
        return;
      }
      
      // Validate: If event dropdown is shown, event must be selected
      if (showEventDropdown && !selectedEventId) {
        addNotification('Please select a Salesforce event before sending tasks', 'error');
        return;
      }

      // AUTO-SAVE: Check if there are unsaved changes and save them first
      const newTasks = tasks.filter(task => task.isNew);
      const existingTasks = tasks.filter(task => !task.isNew);
      const changedTasks = getModifiedTasks(existingTasks, originalTasks);
      const changedTakeaways = getModifiedTakeaways(takeaways, originalTakeaways);
      
      const hasUnsavedChanges = changedTasks.length > 0 || changedTakeaways.length > 0 || newTasks.length > 0;
      
      if (hasUnsavedChanges) {
        console.log('Auto-saving changes before sending to Salesforce...');
        setSendingToSalesforce(false); // Temporarily clear to allow save state
        
        // Save changes first
        const saveSuccess = await handleSaveChanges();
        
        if (!saveSuccess) {
          console.error('Auto-save failed, aborting Salesforce send');
          addNotification('Failed to save changes. Cannot send to Salesforce until data is saved.', 'error');
          return; // Abort Salesforce send
        }
        
        // Re-enable sending state
        setSendingToSalesforce(true);
        
        console.log('Auto-save completed. Proceeding to send to Salesforce...');
      }

      // Prepare task data - send only id and meetingId for backend enrichment
      const taskData = selectedTasks.map(task => ({
        id: task.id,
        meetingId: task.meetingId || meetingId // Use task's meetingId if available, fallback to store
      }));

      // Prepare request body with meeting context (TLDR, Executive Summary, Detailed Summary, and tasks)
      const requestBody: { 
        meetingContext: {
          tldr: string;
          executiveSummary: string;
          detailedSummary: string;
          tasks: any[];
        };
        selectedEventId?: string;
      } = {
        meetingContext: {
          tldr: tldr,
          executiveSummary: executiveSummary,
          detailedSummary: detailedSummary,
        tasks: taskData
        }
      };
      
      // Always include event ID: use resolvedEventId if available, otherwise user-selected
      const effectiveEventId = resolvedEventId || selectedEventId;
      if (effectiveEventId) {
        requestBody.selectedEventId = effectiveEventId;
      }

      // Send to Salesforce endpoint
      const response = await fetch('/api/internal/salesforce/send', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include',
        body: JSON.stringify(requestBody)
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || `Failed to send to Salesforce: HTTP ${response.status}`);
      }

      const result = await response.json();
      console.log('Salesforce send completed:', result);
      
      // New response format: { totalTasks, successCount, errorCount, results: [...] }
      const { results = [] } = result;
      
      // Separate successes and failures
      const successfulTasks = results.filter((r: any) => r.success);
      const failedTasks = results.filter((r: any) => !r.success);
      
      // Clear successful task selections
      if (successfulTasks.length > 0) {
        const successfulTaskIds = successfulTasks.map((r: any) => r.taskId);
        const newSelection = new Set(selectedItemIds);
        successfulTaskIds.forEach((id: string) => newSelection.delete(id));
        setSelectedItemIds(newSelection);
        
        // Reload the grid data from the backend to get the updated lock status
        if (meetingId) {
          console.log('Reloading grid data after Salesforce send...');
          const data = await meetingDataService.loadMeeting(meetingId);
          loadFromJson(data as any, meetingId);
          console.log('Grid data reloaded successfully');
        }
      }
      
      // Build SUCCESS message - list ALL successfully saved tasks (green box)
      const successLines: string[] = [];
      if (successfulTasks.length > 0) {
        successfulTasks.forEach((r: any) => {
          const taskName = r.taskTitle || r.taskId;
          successLines.push(`✓ Successfully saved task: ${taskName}`);
        });
      }
      
      // Build ERROR message - collect ALL errors (task failures + event note errors) (red box)
      const errorLines: string[] = [];
      
      // Add task failures
      failedTasks.forEach((r: any) => {
            const taskName = r.taskTitle || r.taskId;
            const errorMsg = r.errorMessage || 'Unknown error';
        errorLines.push(`✗ Error saving task: ${taskName} - "${errorMsg}"`);
      });
      
      // Add event note errors from successful tasks
      successfulTasks.forEach((r: any) => {
        if (r.eventNoteUpdateError?.error?.message) {
          const taskName = r.taskTitle || r.taskId;
          const eventNoteError = r.eventNoteUpdateError.error.message;
          errorLines.push(`✗ Error saving task: ${taskName} - "${eventNoteError}"`);
        }
      });
      
      // Show notifications using queue (stacked)
      if (successLines.length > 0) {
        addNotification(successLines.join('\n'), 'success');
      }
      if (errorLines.length > 0) {
        addNotification(errorLines.join('\n'), 'error');
      }
    } catch (error) {
      console.error('Error sending to Salesforce:', error);
      const errorMsg = error instanceof Error ? error.message : 'Unknown error';
      
      // Check if it's a timeout error
      if (errorMsg.includes('timeout') || errorMsg.includes('timed out')) {
        addNotification('Failed to send to Salesforce: Request timed out after 60 seconds. The webhook did not respond in time.', 'error');
      } else {
        addNotification(`Failed to send to Salesforce: ${errorMsg}`, 'error');
      }
    } finally {
      setSendingToSalesforce(false);
    }
  };

  return (
    <Paper elevation={2} sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      <Box sx={{ 
        borderBottom: 1, 
        borderColor: 'divider', 
        display: 'flex', 
        alignItems: 'center', 
        justifyContent: 'space-between',
        px: 2,
        py: 1
      }}>
        {/* Left side: Filter dropdown and Event dropdown */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <FormControl size="small" sx={{ minWidth: 150 }}>
              <Select
                value={filterType}
                onChange={handleFilterChange}
                displayEmpty
              >
                <MenuItem value="All">All</MenuItem>
                <MenuItem value="Tasks">Tasks</MenuItem>
                <MenuItem value="Takeaways">Takeaways</MenuItem>
              </Select>
            </FormControl>
            
            {/* Salesforce Event Dropdown - only shown if no Chorus metadata */}
            {showEventDropdown && (
              <FormControl size="small" sx={{ minWidth: 250 }} required error={!selectedEventId}>
                <InputLabel id="event-select-label">Select Event *</InputLabel>
                <Select
                  labelId="event-select-label"
                  value={selectedEventId}
                  onChange={handleEventChange}
                  label="Select Event *"
                  disabled={loadingEvents}
                >
                  {loadingEvents && (
                    <MenuItem value="" disabled>
                      Loading events...
                    </MenuItem>
                  )}
                  {!loadingEvents && events.length === 0 && (
                    <MenuItem value="" disabled>
                      No events found in past 30 days
                    </MenuItem>
                  )}
                  {events.map((event) => (
                    <MenuItem key={event.id} value={event.id}>
                      {event.subject} {event.activityDate ? `(${new Date(event.activityDate).toLocaleDateString()})` : ''}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            )}
        </Box>
        
        {/* Right side: Data Controls */}
        <Box sx={{ display: 'flex', gap: 1 }}>
          <DataControls 
            compact 
            selectedTaskIds={selectedItemIds}
            currentTab={0} // No longer using tabs
            onSendToSalesforce={handleSendToSalesforce}
            sendingToSalesforce={sendingToSalesforce}
            showEventDropdown={showEventDropdown}
            selectedEventId={selectedEventId}
            onAddTask={handleAddTask}
          />
        </Box>
      </Box>
      
      <Box sx={{ flex: 1, overflow: 'hidden', p: 2 }}>
              <ItemsGrid 
                filterType={filterType}
                selectedItemIds={selectedItemIds}
                onSelectedItemIdsChange={setSelectedItemIds}
                onDeleteItem={handleDeleteItem}
              />
      </Box>
      
      {/* Stacked Notifications Container */}
      <Box
        sx={{
          position: 'fixed',
          top: '80px',
          left: '50%',
          transform: 'translateX(-50%)',
          zIndex: (theme) => theme.zIndex.snackbar,
          display: 'flex',
          flexDirection: 'column',
          gap: '10px',
          pointerEvents: 'none', // Allow clicks through the container
        }}
      >
        {notifications.filter(n => n.open).map((notification) => {
          const handleClose = (e: React.SyntheticEvent) => {
            e.preventDefault();
            e.stopPropagation();
            closeNotification(notification.id);
          };
          
          return (
        <Alert 
              key={notification.id}
              onClose={handleClose}
              severity={notification.severity}
              sx={{ 
                minWidth: '500px',
                maxWidth: '800px',
                whiteSpace: 'pre-line',
                pointerEvents: 'auto', // Re-enable clicks on the alert itself
              }}
        >
              {notification.message}
        </Alert>
          );
        })}
      </Box>
      
      {/* Full-page loading overlay for saving */}
      <Backdrop
        sx={{ 
          color: '#fff', 
          zIndex: (theme) => theme.zIndex.drawer + 1,
          flexDirection: 'column',
          gap: 2
        }}
        open={saving}
      >
        <CircularProgress color="inherit" size={60} />
        <Typography variant="h6">
          Saving changes...
        </Typography>
        <Typography variant="body2">
          Please wait while we save and reload your data
        </Typography>
      </Backdrop>
      
      {/* Full-page loading overlay for Salesforce send */}
      <Backdrop
        sx={{ 
          color: '#fff', 
          zIndex: (theme) => theme.zIndex.drawer + 1,
          flexDirection: 'column',
          gap: 2
        }}
        open={sendingToSalesforce}
      >
        <CircularProgress color="inherit" size={60} />
        <Typography variant="h6">
          Sending to Salesforce...
        </Typography>
        <Typography variant="body2">
          Please wait, this may take up to 60 seconds
        </Typography>
      </Backdrop>

      {/* Add Task Modal */}
      <AddTaskModal
        open={addTaskModalOpen}
        onClose={() => setAddTaskModalOpen(false)}
        onAdd={handleAddTaskSubmit}
        salesforceUsers={salesforceUsers}
      />

      {/* Delete Confirmation Dialog */}
      <DeleteConfirmDialog
        open={deleteDialogOpen}
        onClose={() => {
          setDeleteDialogOpen(false);
          setItemToDelete(null);
        }}
        onConfirm={handleDeleteConfirm}
        itemType={itemToDelete?.type || 'task'}
        itemName={itemToDelete?.name || ''}
      />
    </Paper>
  );
};

export default GridTabs;
